import { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";

import { Modal } from "./Modal";
import { Button } from "./Button";
import { getCroppedSquareImage, type PixelCrop } from "~/lib/avatar-crop";

type AvatarCropModalProps = {
  /** Object URL of the picked image to crop. */
  src: string;
  /** True while the resulting blob is being uploaded. */
  busy?: boolean;
  title: string;
  instructions: string;
  zoomLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Copy shown if the canvas export fails. */
  errorLabel: string;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
};

/**
 * T278 — RRSS-style square avatar cropper. The member pans/zooms the image
 * inside a round frame; on confirm the chosen square region is exported to a
 * JPEG blob and handed back to the caller (which uploads it through the existing
 * avatar action). Wraps `Modal` to inherit focus-trap / Escape / restore-focus
 * (the T180 a11y contract).
 */
export function AvatarCropModal({
  src,
  busy = false,
  title,
  instructions,
  zoomLabel,
  confirmLabel,
  cancelLabel,
  errorLabel,
  onCancel,
  onConfirm,
}: AvatarCropModalProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState<PixelCrop | null>(null);
  const [failed, setFailed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const active = useRef(true);
  const applying = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const cancel = () => { active.current = false; onCancel(); };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixels(areaPixels);
  }, []);

  const apply = async () => {
    if (!pixels || busy || applying.current) return;
    applying.current = true;
    setProcessing(true);
    setFailed(false);
    try {
      const blob = await getCroppedSquareImage(src, pixels);
      if (active.current) onConfirm(blob);
    } catch {
      if (active.current) setFailed(true);
    } finally {
      applying.current = false;
      if (active.current) setProcessing(false);
    }
  };

  return (
    <Modal open onClose={cancel} showCloseButton={false} ariaLabel={title}>
      <div className="flex flex-col gap-[var(--tfc-gap-md)]">
        <div className="flex flex-col gap-1">
          <h2 className="pf-block-title" style={{ margin: 0 }}>
            {title}
          </h2>
          <p className="pf-block-note" style={{ margin: 0 }}>
            {instructions}
          </p>
        </div>

        <div className="relative h-[280px] w-full overflow-hidden rounded-[var(--tfc-radius-lg)] bg-darkBg">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <label className="flex items-center gap-3">
          <span className="pf-block-note" style={{ margin: 0, whiteSpace: "nowrap" }}>
            {zoomLabel}
          </span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            aria-label={zoomLabel}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-[var(--color-textMain)]"
          />
        </label>

        {failed ? (
          <p className="pf-block-note text-danger" role="alert" style={{ margin: 0 }}>
            {errorLabel}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" type="button" onClick={cancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant="dark"
            type="button"
            onClick={apply}
            disabled={busy || !pixels}
            isLoading={busy || processing}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
